import { calculatePrintArea } from '.';
import { useStore as useColorManagementStore } from '@/stores/colorsManagement';
import { useStore as useShapeStore } from '@/stores/shape';
import { useStore as useProductStore } from '@/stores/product';
import { useStore as useColorManagementStoreV2 } from '@/stores/v2/colorsManagement';
import { useStore as useShapeStoreV2 } from '@/stores/v2/shape';
import { useStore as useProductStoreV2 } from '@/stores/v2/product';
import { PrintTechnique } from '@midocean/webapps-shared-domain';

export type PriceParamsEvent = {
  printPositionSAPCode: string;
  printTechniqueSAPCode: string;
  printTechniqueSAPRangeCode: string;
  numberOfColors: number;
  width: number;
  height: number;
};

export const generateEventPayload = (): PriceParamsEvent[] => {
  const colorManagementStore = useColorManagementStore();
  const shapeStore = useShapeStore();
  const productStore = useProductStore();

  return productStore.unavailablePrintPositions.map((p) => {
    const printPosition = productStore.data.productPrintPositions.find(
      (pp) => pp.id === p.positionId
    );
    const printTechnique = printPosition?.printTechniques.find(
      (t) => t.id === p.technique.id
    ) as PrintTechnique;

    const positions = shapeStore.positionShapeMap.filter((position: any) => {
      return position.id === p.positionId;
    });

    const { width, height } = calculatePrintArea(
      printPosition?.originalImageWebDpi || 300,
      positions,
      shapeStore.shapes
    );

    const numberOfColours =
      colorManagementStore.getNumberOfColoursByPrintPositionId(p.positionId);

    const eventPayload = {
      printPositionSAPCode: p.positionName,
      printTechniqueSAPCode: printTechnique?.printTechnique.code,
      printTechniqueSAPRangeCode: determineRangeSAPCode(
        Math.round(width),
        Math.round(height),
        printTechnique
      ),
      numberOfColors: numberOfColours,
      width: width,
      height: height,
    };
    return eventPayload;
  });
};

export const generateEventPayloadV2 = (): PriceParamsEvent[] => {
  const colorManagementStore = useColorManagementStoreV2();
  const shapeStore = useShapeStoreV2();
  const productStore = useProductStoreV2();

  return productStore.unavailablePrintPositions.map((p) => {
    const printPosition = productStore.data.productPrintPositions.find(
      (pp) => pp.id === p.positionId
    );
    const printTechnique = printPosition?.printTechniques.find(
      (t) => t.id === p.technique.id
    ) as PrintTechnique;

    const positions = shapeStore.positionShapeMap.filter((position: any) => {
      return position.id === p.positionId;
    });

    const { width, height } = calculatePrintArea(
      printPosition?.originalImageWebDpi || 300,
      positions,
      shapeStore.shapes
    );

    const numberOfColours =
      colorManagementStore.getNumberOfColoursByPrintPositionId(p.positionId);

    const eventPayload = {
      printPositionSAPCode: p.positionName,
      printTechniqueSAPCode: printTechnique?.printTechnique.code,
      printTechniqueSAPRangeCode: determineRangeSAPCode(
        Math.round(width),
        Math.round(height),
        printTechnique
      ),
      numberOfColors: numberOfColours,
      width: width,
      height: height,
    };
    return eventPayload;
  });
};

export const determineRangeSAPCode = (
  printAreaWidth: number,
  printAreaHeight: number,
  printTechnique: PrintTechnique
): string => {
  const rangeComparisonValueInCm2 = printAreaWidth * printAreaHeight * 0.01;

  if (printTechnique?.printTechnique?.ranges.length === 0) {
    return printTechnique.printTechnique.code;
  }

  const chosenRange = printTechnique?.printTechnique?.ranges?.find((r) => {
    return (
      rangeComparisonValueInCm2 >= r.minRangeCm2 &&
      rangeComparisonValueInCm2 <= r.maxRangeCm2
    );
  });

  return chosenRange === undefined ||
    (chosenRange.minRangeCm2 === 0 && chosenRange.maxRangeCm2 === 0)
    ? printTechnique?.printTechnique?.code
    : chosenRange.code;
};
